import { CalendarDays, Download } from "lucide-react";
import { calendarUrl } from "../../api/mailbox";
import type { MailCalendarEvent } from "../../api/types";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { formatPeriod } from "../quotes/EventPeriodField";

// 信裡的行事曆邀請(`text/calendar` / 附上的 .ics)顯示成卡片:主題、時間、地點、主辦人,
// 可下載 .ics(規格 §2.6)。解析在後端(`render.parse_calendar`),這裡只排版。
//
// 時間的三種樣子(後端 `_when`):
// - 全天 `2026-12-12` → 與專案時間同一套寫法 `2026/12/12(Sat)-12/13(Sun)`;
//   iCalendar 全天活動的 DTEND 是「隔天」(不含),顯示時退一天。
// - 帶時差 → 換成台北時間。
// - 不帶時差(認不得的時區、浮動時間)→ 照寫的時間顯示,不換算。

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const FLOATING = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

function dayBefore(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, d - 1));
  return at.toISOString().slice(0, 10);
}

function moment(value: string): string {
  if (/(?:Z|[+-]\d{2}:?\d{2})$/.test(value)) return formatTaipeiDateTime(value);
  const m = FLOATING.exec(value);
  return m ? `${m[1]}/${m[2]}/${m[3]} ${m[4]}:${m[5]}` : value;
}

export function formatEventTime(start: string, end: string): string {
  if (DATE_ONLY.test(start)) {
    const last = DATE_ONLY.test(end) && end > start ? dayBefore(end) : start;
    return formatPeriod(start, last);
  }
  if (!start) return "";
  const from = moment(start);
  const to = end ? moment(end) : "";
  if (!to) return from;
  // 同一天只寫一次日期
  return to.slice(0, 10) === from.slice(0, 10) ? `${from}–${to.slice(11)}` : `${from} – ${to}`;
}

export function CalendarCard({ messageId, events }: { messageId: number; events: MailCalendarEvent[] | undefined }) {
  const event = (events ?? [])[0];
  if (!event) return null;
  const when = formatEventTime(event.start, event.end);
  return (
    <section
      aria-label="行事曆邀請"
      className="flex flex-col gap-1.5 rounded-[var(--radius)] border border-[var(--accent)] bg-[var(--accent-soft)] p-3 text-sm text-[var(--text)]"
    >
      <h4 className="m-0 flex items-center gap-1.5 text-base font-semibold">
        <CalendarDays aria-hidden="true" size={16} />
        {event.summary || "(沒有主題的活動)"}
      </h4>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        {when ? (
          <>
            <dt className="text-[var(--text-3)]">時間</dt>
            <dd className="m-0 tabular">{when}</dd>
          </>
        ) : null}
        {event.location ? (
          <>
            <dt className="text-[var(--text-3)]">地點</dt>
            <dd className="m-0">{event.location}</dd>
          </>
        ) : null}
        {event.organizer ? (
          <>
            <dt className="text-[var(--text-3)]">主辦人</dt>
            <dd className="m-0">{event.organizer}</dd>
          </>
        ) : null}
      </dl>
      <a
        href={calendarUrl(messageId)}
        download="invite.ics"
        className="inline-flex items-center gap-1.5 self-start text-sm text-[var(--accent-hover)] underline-offset-2 hover:underline print:hidden"
      >
        <Download aria-hidden="true" size={14} />
        下載 .ics(加進行事曆)
      </a>
    </section>
  );
}
